import { EppoClient, IAssignmentEvent } from '@eppo/js-client-sdk-common'
import {
  DefaultSubjectAttributes,
  transformSubjectAttributes,
} from '../../shared/DefaultSubjectAttributes'
import {
  resolveEppoSubjectKey,
  tryResolveEppoSubjectKey,
} from '../../shared/resolveEppoSubjectKey'

type Attributes = {
  [key: string]: string | number | boolean
}

export type GetExperimentVariantParams = {
  subjectAttributes?: Attributes
}

export type GetExperiment = <R>(
  experimentKey: string,
  args: { fallback: R; subjectAttributes?: Attributes },
) => R

export const setupGetExperimentVariant = ({
  client,
  sessionId,
  customerId,
  defaultAttributes,
  overrides,
}: {
  client: EppoClient | null
  sessionId: string
  customerId: string | null
  defaultAttributes: DefaultSubjectAttributes
  overrides: { [key: string]: string }
}) => {
  const trackedServerExperiments: Record<
    string,
    string | boolean | number | object
  > = {}

  client?.setAssignmentLogger({
    logAssignment: (assignmentEvent: IAssignmentEvent) => {
      if (!assignmentEvent.experiment) return
      if (!assignmentEvent.variation) return
      if (assignmentEvent.subjectAttributes?.shouldLog === false) return
      trackedServerExperiments[assignmentEvent.experiment] =
        assignmentEvent.variation
    },
  })

  const flagConfigurations = client?.getFlagConfigurations()

  const getSubjectKey = (experimentKey: string) => {
    if (!client) return null

    const flagConfiguration = flagConfigurations?.[experimentKey]
    return flagConfiguration
      ? resolveEppoSubjectKey(flagConfiguration, sessionId, customerId)
      : sessionId
  }

  const canSendExperimentToServer = (experimentKey: string) => {
    if (!client) return true
    if (overrides[experimentKey]) return true

    const flagConfiguration = flagConfigurations?.[experimentKey]
    if (!flagConfiguration) return true

    return (
      tryResolveEppoSubjectKey(flagConfiguration, sessionId, customerId) !==
      null
    )
  }

  const getExperimentString: GetExperiment = (
    experimentKey: string,
    { fallback, subjectAttributes },
  ) => {
    if (!client) return fallback
    if (overrides[experimentKey])
      return overrides[experimentKey] as unknown as typeof fallback

    const subjectKey = getSubjectKey(experimentKey)
    if (!subjectKey) return fallback

    return client.getStringAssignment(
      experimentKey,
      subjectKey,
      transformSubjectAttributes(defaultAttributes, subjectAttributes),
      fallback as unknown as string,
    ) as unknown as typeof fallback
  }

  const getExperimentBoolean: GetExperiment = (
    experimentKey: string,
    { fallback, subjectAttributes },
  ) => {
    if (!client) return fallback
    if (overrides[experimentKey])
      return (overrides[experimentKey] === 'true') as unknown as typeof fallback

    const subjectKey = getSubjectKey(experimentKey)
    if (!subjectKey) return fallback

    return client.getBooleanAssignment(
      experimentKey,
      subjectKey,
      transformSubjectAttributes(defaultAttributes, subjectAttributes),
      fallback as unknown as boolean,
    ) as unknown as typeof fallback
  }

  const getExperimentJson: GetExperiment = (
    experimentKey: string,
    { fallback, subjectAttributes },
  ) => {
    if (!client) return fallback
    if (overrides[experimentKey])
      return JSON.parse(overrides[experimentKey]) as unknown as typeof fallback

    const subjectKey = getSubjectKey(experimentKey)
    if (!subjectKey) return fallback

    return client.getJSONAssignment(
      experimentKey,
      subjectKey,
      transformSubjectAttributes(defaultAttributes, subjectAttributes),
      fallback as unknown as object,
    ) as unknown as typeof fallback
  }

  const getExperimentInteger: GetExperiment = (
    experimentKey: string,
    { fallback, subjectAttributes },
  ) => {
    if (!client) return fallback
    if (overrides[experimentKey])
      return Number(overrides[experimentKey]) as unknown as typeof fallback

    const subjectKey = getSubjectKey(experimentKey)
    if (!subjectKey) return fallback

    return client.getIntegerAssignment(
      experimentKey,
      subjectKey,
      transformSubjectAttributes(defaultAttributes, subjectAttributes),
      fallback as unknown as number,
    ) as unknown as typeof fallback
  }

  const getExperimentNumeric: GetExperiment = (
    experimentKey: string,
    { fallback, subjectAttributes },
  ) => {
    if (!client) return fallback
    if (overrides[experimentKey])
      return Number(overrides[experimentKey]) as unknown as typeof fallback

    const subjectKey = getSubjectKey(experimentKey)
    if (!subjectKey) return fallback

    return client.getNumericAssignment(
      experimentKey,
      subjectKey,
      transformSubjectAttributes(defaultAttributes, subjectAttributes),
      fallback as unknown as number,
    ) as unknown as typeof fallback
  }

  return {
    getExperimentString,
    getExperimentBoolean,
    getExperimentJson,
    getExperimentInteger,
    getExperimentNumeric,
    trackedServerExperiments,
    canSendExperimentToServer,
  }
}
